import { z } from "src/lib";
import { FilePutFields } from "../api/file-fields";
import { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from "@mui/material";
import { DropzoneField } from "src/components/forms/DropzoneField";
import toast from "react-hot-toast";
import { AsyncSelect, ControlledTextField } from "src/components/forms";
import { fileFormatter } from "src/utils/formatters/fileFormatter";
import { Close } from "@mui/icons-material";
import { HttpSistema } from "../api/httpSistema";

export const schemaNuevoArchivo = z.object({
    [FilePutFields.CodArchivoTipo]: z.number(),
    [FilePutFields.CodPais]: z.number(),
    [FilePutFields.TieneArchivoFisico]: z.boolean(),
    [FilePutFields.File]: z.instanceof(File),
    [FilePutFields.Observaciones]: z.string().optional()
})

export type SchemaNuevoArchivo = z.infer<typeof schemaNuevoArchivo>

type DialogNuevoArchivoProps = {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    idOperacion: number
    idPais: number  // TODO: a CodigoSistemaPrinciapl
    codSistema: number
}

export const DialogNuevoArchivo = ({ open, setOpen, idPais, idOperacion, codSistema }: DialogNuevoArchivoProps) => {

    const { control, setValue, handleSubmit, watch, resetField } = useForm<SchemaNuevoArchivo>({
        resolver: zodResolver(schemaNuevoArchivo),
        defaultValues: {
            [FilePutFields.CodPais]: idPais,
            [FilePutFields.TieneArchivoFisico]: false
        }
    })

    const file = watch(FilePutFields.File)

    const alGuardarArchivo = (data: SchemaNuevoArchivo) => {
        const promise = HttpSistema.postArchivo(idOperacion, codSistema, data)
            .then(r => {
                if (r.tieneError) {
                    return Promise.reject(r.descripcionError);
                }

                return r;
            });

        toast.promise(promise, {
            loading: 'Guardando',
            success: 'Guardado correctamente',
            error: (err) => `Error al guardar: ${err}`
        })
            .then(() => setOpen(false))
            .catch((e) => {
                console.log(e)
            });
    }

    // TODO: botones bloqueados mientras hace la accion, con un spinner
    return (
        <Dialog open={open} maxWidth='sm' fullWidth>
            <DialogTitle>Nuevo archivo</DialogTitle>
            <DialogContent>
                <Grid container spacing={4} pt={2}>
                    <Grid item xs={12}>
                        <AsyncSelect
                            control={control}
                            name={FilePutFields.CodArchivoTipo}
                            label='Tipo de archivo'
                            loadOptions={HttpSistema.getArchivoTipos}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name={FilePutFields.Observaciones}
                            label='Observaciones'
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {
                            file ?
                                <Box
                                    display='flex'
                                    justifyContent='space-between'
                                    alignItems='center'
                                >
                                    <Box flexGrow={1} display='flex' justifyContent='left' alignItems='center'>
                                        {fileFormatter.getIconByFileName(file.name, { fontSize: 'large' })}
                                        <Stack pl={2}>
                                            <Typography variant='body2'>{file.name}</Typography>
                                        </Stack>
                                    </Box>
                                    <IconButton onClick={() => resetField(FilePutFields.File)}
                                    >
                                        <Close fontSize='small' />
                                    </IconButton>
                                </Box>
                                :
                                <DropzoneField
                                    name={FilePutFields.File}
                                    control={control}
                                    setValue={setValue}
                                />
                        }
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='primary' onClick={() => setOpen(false)}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarArchivo)}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog >
    )
}

interface ArchivoFormDataProps {
    archivo: File,
    onRemove?: () => void
}

export const ArchivoFormData = ({ archivo, onRemove }: ArchivoFormDataProps) => {

    return (
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
          <Box flexGrow={1} display='flex' justifyContent='left' alignItems='center'>
              {fileFormatter.getIconByFileName(archivo.name, { fontSize: 'large' })}
              <Stack pl={2}>
                  <Typography variant='body2'>{archivo.name}</Typography>
              </Stack>
          </Box>
          {
              onRemove &&
            <IconButton onClick={onRemove}
            >
                <Close fontSize='small' />
            </IconButton>
          }

      </Box>
      )

}